import React, { useState } from 'react';
import { t, tList } from '../i18n';

// ── SVG icons ────────────────────────────────────────────────────────────────

const BackChevron = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// ── Types ────────────────────────────────────────────────────────────────────

export interface IntakeData {
  first_name: string;
  email:      string;
  phone:      string;
  concerns:   string[];
  age:        string;
  // NOTE: skin_type intentionally maps to question_key 'q5' for backward compatibility
  // with existing lead_answer data. Semantically misleading — q5 reads like a numbered
  // question but stores skin type. Must remain 'q5' to avoid breaking existing records.
  skin_type:  string;
  q1:         string;
  q2:         string;
  q3:         string;
  q4:         string;
}

export const EMPTY_INTAKE: IntakeData = {
  first_name: '', email: '', phone: '', concerns: [],
  age: '', skin_type: '', q1: '', q2: '', q3: '', q4: '',
};

interface Stage02IntakeFlowProps {
  clinicConfig: {
    brand_name: string;
    logo_url?: string;
    privacy_policy_url?: string;
  };
  initialSubStep?: number;
  initialData?: IntakeData;
  onSubStepChange?: (step: number) => void;
  onAdvance: (data: IntakeData) => void;
  onBack: () => void;
}

// ── Static data ───────────────────────────────────────────────────────────────
//
// All user-visible copy is resolved at render time via the i18n catalog
// (`t` / `tList`) so it follows the clinic locale. Step labels and question
// prompts are keyed per sub-step below.

const STEP_LABEL_KEYS = [
  'q.step.profile',
  'q.step.q1',
  'q.step.q2',
  'q.step.q3',
  'q.step.q4',
] as const;

const Q_PROMPT_KEYS = ['q1.prompt', 'q2.prompt', 'q3.prompt', 'q4.prompt'] as const;

// ── QuestionScreen — reused for sub-steps 2–5 ────────────────────────────────

interface QuestionScreenProps {
  prompt:   string;
  options:  readonly string[];
  selected: string;
  helper?:  string;
  onSelect: (value: string) => void;
  onNext:   () => void;
}

const QuestionScreen = ({ prompt, options, selected, helper, onSelect, onNext }: QuestionScreenProps) => (
  <>
    <h2 className="vc-title vc-title--md" role="heading" aria-level={2}>{prompt}</h2>
    {helper && <p className="vc-sub">{helper}</p>}
    <div className="vc-options-r2" role="listbox" aria-label={prompt}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          role="option"
          aria-selected={selected === opt}
          className={`vc-option-r2${selected === opt ? ' is-selected' : ''}`}
          onClick={() => onSelect(opt)}
        >
          {opt}
          <i className="vc-node" aria-hidden="true"></i>
        </button>
      ))}
    </div>
    <div className="vc-cta-zone">
      <button
        type="button"
        className="vc-btn vc-btn--primary"
        disabled={!selected}
        aria-disabled={!selected}
        onClick={onNext}
      >
        {t('common.continue')}
      </button>
    </div>
  </>
);

// ── Main component ────────────────────────────────────────────────────────────

export const Stage02IntakeFlow = ({
  clinicConfig: _clinicConfig,
  initialSubStep = 1,
  initialData = EMPTY_INTAKE,
  onSubStepChange,
  onAdvance,
  onBack,
}: Stage02IntakeFlowProps) => {
  const [subStep, setSubStep]       = useState(initialSubStep);
  const [intakeData, setIntakeData] = useState<IntakeData>(initialData);

  const stepLabel = t(STEP_LABEL_KEYS[subStep - 1]);

  const update = (patch: Partial<IntakeData>) =>
    setIntakeData((prev) => ({ ...prev, ...patch }));

  const changeSubStep = (next: number | ((prev: number) => number)) => {
    const value = typeof next === 'function' ? next(subStep) : next;
    setSubStep(value);
    // Notify the parent outside the setState updater — calling it inside
    // triggers React's setState-in-render warning.
    onSubStepChange?.(value);
  };

  const handleBack = () => {
    if (subStep === 1) {
      onBack();
    } else {
      changeSubStep((s) => s - 1);
    }
  };

  const nextSubStep = (patch?: Partial<IntakeData>) => {
    const merged = patch ? { ...intakeData, ...patch } : intakeData;
    setIntakeData(merged);
    if (subStep < 5) {
      changeSubStep((s) => s + 1);
    } else {
      onAdvance(merged);
    }
  };

  const handleConcernToggle = (concern: string) => {
    setIntakeData((prev) => ({
      ...prev,
      concerns: prev.concerns.includes(concern)
        ? prev.concerns.filter((c) => c !== concern)
        : [...prev.concerns, concern],
    }));
  };

  // ── Sub-step renderers ──────────────────────────────────────────────────────

  const renderSubStep = () => {
    switch (subStep) {

      case 1:
        return (
          <>
            <h2 className="vc-title" role="heading" aria-level={2}>
              {t('profile.title.pre')}<em>{t('profile.title.em')}</em>{t('profile.title.post')}
            </h2>
            <p className="vc-sub">{t('profile.sub')}</p>

            <div className="vc-chip-groups">
              <div className="vc-chip-group">
                <div className="vc-fieldlabel">
                  {t('profile.ageLabel')} <span className="req" aria-hidden="true">{t('profile.required')}</span>
                </div>
                <div
                  role="listbox"
                  aria-required="true"
                  aria-label={t('profile.ageAria')}
                  className="vc-chips"
                >
                  {tList('profile.ageRanges').map((age) => (
                    <button
                      key={age}
                      type="button"
                      role="option"
                      aria-selected={intakeData.age === age}
                      className={`vc-chip-r2${intakeData.age === age ? ' is-selected' : ''}`}
                      onClick={() => update({ age })}
                    >
                      <i className="vc-node" aria-hidden="true"></i>
                      {age}
                    </button>
                  ))}
                </div>
              </div>

              <div className="vc-chip-group">
                <div className="vc-fieldlabel">
                  {t('profile.skinTypeLabel')} <span>{t('profile.optional')}</span>
                </div>
                <div role="listbox" aria-label={t('profile.skinTypeAria')} className="vc-chips">
                  {tList('profile.skinTypes').map((type) => (
                    <button
                      key={type}
                      type="button"
                      role="option"
                      aria-selected={intakeData.skin_type === type}
                      className={`vc-chip-r2${intakeData.skin_type === type ? ' is-selected' : ''}`}
                      onClick={() =>
                        update({ skin_type: intakeData.skin_type === type ? '' : type })
                      }
                    >
                      <i className="vc-node" aria-hidden="true"></i>
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="vc-chip-group">
                <div className="vc-fieldlabel">
                  {t('profile.concernsLabel')} <span>{t('profile.optional')}</span>
                </div>
                <div className="vc-help">{t('profile.concernsHelp')}</div>
                <div
                  role="listbox"
                  aria-multiselectable="true"
                  aria-label={t('profile.concernsAria')}
                  className="vc-chips"
                >
                  {tList('profile.skinConcerns').map((concern) => (
                    <button
                      key={concern}
                      type="button"
                      role="option"
                      aria-selected={intakeData.concerns.includes(concern)}
                      className={`vc-chip-r2${intakeData.concerns.includes(concern) ? ' is-selected' : ''}`}
                      onClick={() => handleConcernToggle(concern)}
                    >
                      <i className="vc-node" aria-hidden="true"></i>
                      {concern}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="vc-cta-zone">
              <button
                type="button"
                className="vc-btn vc-btn--primary"
                disabled={!intakeData.age}
                aria-disabled={!intakeData.age}
                onClick={() => nextSubStep()}
              >
                {t('common.continue')}
              </button>
            </div>
          </>
        );

      case 2:
        return (
          <QuestionScreen
            prompt={t(Q_PROMPT_KEYS[0])}
            options={tList('q1.options')}
            selected={intakeData.q1}
            onSelect={(q1) => update({ q1 })}
            onNext={() => nextSubStep()}
          />
        );

      case 3:
        return (
          <QuestionScreen
            prompt={t(Q_PROMPT_KEYS[1])}
            options={tList('q2.options')}
            selected={intakeData.q2}
            onSelect={(q2) => update({ q2 })}
            onNext={() => nextSubStep()}
          />
        );

      case 4:
        return (
          <QuestionScreen
            prompt={t(Q_PROMPT_KEYS[2])}
            options={tList('q3.options')}
            selected={intakeData.q3}
            onSelect={(q3) => update({ q3 })}
            onNext={() => nextSubStep()}
          />
        );

      case 5:
        return (
          <QuestionScreen
            prompt={t(Q_PROMPT_KEYS[3])}
            options={tList('q4.options')}
            selected={intakeData.q4}
            helper={t('q4.helper')}
            onSelect={(q4) => update({ q4 })}
            onNext={() => nextSubStep()}
          />
        );

      default:
        return null;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="vc-frame stage-02-flow">
      <style>{`
        /* Stage 02 — stage-specific layout only.
           Shared chamber vocabulary → widget-components.css */
        .stage-02-flow .vc-chip-groups {
          display: flex;
          flex-direction: column;
          gap: var(--sp-5);
          margin: var(--sp-6) var(--sp-6) 0;
        }
        .stage-02-flow .vc-chip-group {
          display: flex;
          flex-direction: column;
          gap: var(--sp-3);
        }
        .stage-02-flow .vc-chip-group .vc-help {
          margin-top: calc(-1 * var(--sp-2));
        }
        .stage-02-flow .vc-cta-zone {
          margin-top: auto;
          padding-top: var(--sp-6);
          padding-bottom: var(--sp-6);
        }
        .stage-02-flow .substep-content {
          display: flex;
          flex-direction: column;
          flex: 1;
          animation: vc-substep-enter var(--dur-slow) var(--ease-entrance) both;
        }
        @keyframes vc-substep-enter {
          from { opacity: 0; transform: translateY(var(--sp-2)); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stage-02-flow .substep-content {
            animation: vc-screen-fade var(--dur-base) var(--ease-standard) both;
          }
        }
      `}</style>

      <div className="vc-head">
        <button
          className="vc-head-btn"
          onClick={handleBack}
          aria-label={subStep === 1 ? t('profile.backToConsentAria') : t('profile.prevQuestionAria')}
        >
          <BackChevron />
        </button>
        <span className="vc-steplabel">{stepLabel}</span>
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
      </div>

      {/* Five informational dots: profile, Q1–Q4 */}
      <div className="vc-dots" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <i
            key={i}
            className={
              i + 1 === subStep ? 'is-live' : i + 1 < subStep ? 'is-done' : ''
            }
          />
        ))}
      </div>

      <div key={subStep} className="substep-content">
        {renderSubStep()}
      </div>
    </div>
  );
};
